import type { TrainingQuestionDef } from '@hcc/types';
import { QuestionCommonFields } from './QuestionCommonFields';
import { ImageUploadInput } from './ImageUploadInput';
import { HotspotRegionCanvas } from './HotspotRegionCanvas';

type HotspotQuestion = Extract<TrainingQuestionDef, { type: 'hotspot' }>;

/**
 * Hotspot editor: upload the picture, then drag rectangles over the
 * correct area(s). The regions never reach the inductee — the player
 * payload strips them and the tap is graded server-side.
 */
export function HotspotQuestionEditor({
  question,
  onChange,
  graded = true,
}: {
  question: HotspotQuestion;
  onChange: (next: HotspotQuestion) => void;
  /** False hides the points box (an unmarked assessment). */
  graded?: boolean;
}) {
  return (
    <div className="space-y-3">
      <QuestionCommonFields
        prompt={question.prompt}
        points={question.points}
        onPrompt={(prompt) => onChange({ ...question, prompt })}
        onPoints={(points) => onChange({ ...question, points })}
        graded={graded}
      />
      <ImageUploadInput
        label="Picture the inductee taps"
        value={question.image_key || undefined}
        onChange={(url) => onChange({ ...question, image_key: url ?? '', regions: [] })}
      />
      {question.image_key ? (
        <HotspotRegionCanvas
          imageUrl={question.image_key}
          regions={question.regions}
          onChange={(regions) => onChange({ ...question, regions })}
        />
      ) : (
        <p className="text-xs text-fg-muted">
          Upload a picture first, then mark the correct areas.
        </p>
      )}
    </div>
  );
}
